import { format } from "date-fns";
import { MessageCircle } from "lucide-react";
import React from "react";

import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components";
import { useI18n } from "@/context/i18n";

import { TCampaignContact } from "../../campaign-details.types";

const ENRICHED_SUMMARY_KEYS = ["segment", "role", "company_size"] as const;
const INITIAL_MESSAGE_TRUNCATE = 40;

function getEnrichmentStatus(contact: TCampaignContact): "enriched" | "failed" | "pending" {
  if (contact.initial_message) return "enriched";
  if (contact.enrichment_error) return "failed";
  return "pending";
}

function getEnrichedSummary(enriched_data?: Record<string, unknown> | null): string {
  if (!enriched_data || typeof enriched_data !== "object") return "-";
  const parts = ENRICHED_SUMMARY_KEYS.filter((k) => enriched_data[k])
    .map((k) => String(enriched_data[k]));
  return parts.length > 0 ? parts.join(" · ") : "-";
}

function truncateMessage(msg: string | null | undefined, maxLen: number): string {
  if (!msg) return "-";
  return msg.length <= maxLen ? msg : `${msg.slice(0, maxLen)}...`;
}

export interface ICampaignDetailsContactsTableProps {
  contacts: TCampaignContact[];
  isLoading: boolean;
  onTestFlow?: (contact: TCampaignContact) => void;
}

export const CampaignDetailsContactsTable: React.FC<
  ICampaignDetailsContactsTableProps
> = ({ contacts, isLoading, onTestFlow }) => {
  const { t } = useI18n();

  return (
    <Card className="shadow-sm">
      <CardHeader>
        <CardTitle>{t("campaigns.details.contacts.title")}</CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <p className="text-muted-foreground">{t("generic.loading")}</p>
        ) : contacts.length === 0 ? (
          <p className="text-muted-foreground">{t("campaigns.details.contacts.empty")}</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("campaigns.details.contacts.columns.company") || "Empresa"}</TableHead>
                <TableHead>{t("campaigns.details.contacts.columns.first_name") || "Nome"}</TableHead>
                <TableHead>{t("campaigns.details.contacts.columns.last_name") || "Sobrenome"}</TableHead>
                <TableHead>{t("campaigns.details.contacts.columns.email") || "E-mail"}</TableHead>
                <TableHead>{t("campaigns.details.contacts.columns.phone")}</TableHead>
                <TableHead>{t("campaigns.details.contacts.columns.phone_2") || "Telefone 2"}</TableHead>
                <TableHead>{t("campaigns.details.contacts.columns.document_number") || "CNPJ/CPF"}</TableHead>
                <TableHead>{t("campaigns.details.contacts.columns.enrichmentStatus")}</TableHead>
                <TableHead>{t("campaigns.details.contacts.columns.enrichedData")}</TableHead>
                <TableHead>{t("campaigns.details.contacts.columns.initialMessage")}</TableHead>
                <TableHead>{t("campaigns.details.contacts.columns.createdAt")}</TableHead>
                {onTestFlow && <TableHead>{t("generic.actions")}</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {contacts.map((contact) => {
                const status = getEnrichmentStatus(contact);
                return (
                  <TableRow key={contact.id} className="hover:bg-muted/40">
                    <TableCell>{contact.company ?? "-"}</TableCell>
                    <TableCell>{contact.first_name ?? contact.name?.split(" ")[0] ?? "-"}</TableCell>
                    <TableCell>{contact.last_name ?? contact.name?.split(" ").slice(1).join(" ") ?? "-"}</TableCell>
                    <TableCell>{contact.email ?? "-"}</TableCell>
                    <TableCell>{contact.phone_e164 ?? "-"}</TableCell>
                    <TableCell>{contact.phone_2_e164 ?? "-"}</TableCell>
                    <TableCell>{contact.document_number ?? "-"}</TableCell>
                    <TableCell>
                      {status === "enriched" && (
                        <Badge variant="default">{t("campaigns.details.contacts.enrichment.enriched")}</Badge>
                      )}
                      {status === "failed" && (
                        <Badge
                          variant="destructive"
                          title={contact.enrichment_error ?? undefined}
                          className="cursor-help"
                        >
                          {t("campaigns.details.contacts.enrichment.failed")}
                        </Badge>
                      )}
                      {status === "pending" && (
                        <Badge variant="outline">{t("campaigns.details.contacts.enrichment.pending")}</Badge>
                      )}
                    </TableCell>
                    <TableCell>{getEnrichedSummary(contact.enriched_data)}</TableCell>
                    <TableCell title={contact.initial_message ?? undefined} className="max-w-[200px]">
                      <span className="line-clamp-2">
                        {truncateMessage(contact.initial_message, INITIAL_MESSAGE_TRUNCATE)}
                      </span>
                    </TableCell>
                    <TableCell>
                      {format(new Date(contact.created_at), "dd/MM/yyyy HH:mm")}
                    </TableCell>
                    {onTestFlow && (
                      <TableCell className="whitespace-nowrap">
                        {status === "enriched" && (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="gap-1.5"
                            onClick={() => onTestFlow(contact)}
                          >
                            <MessageCircle className="h-4 w-4" />
                            {t("campaigns.details.contacts.testFlow")}
                          </Button>
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
};
